/**
 * Helpers for CONNECTION_SAME_SEAT: one seat pick replicated across connection legs.
 */

/**
 * Copies each first-leg selected seat onto every connection trip slug.
 * Downstream purchase still expects one seat entry per leg tripSlug.
 *
 * @param {Array<object>} firstLegSeats - Seats selected on the displayed (first) map.
 * @param {Array<{id: string}>} trips - Connection trips in bus order (`id` = trip slug).
 * @returns {Array<object>} Seats with `tripSlug` set per leg.
 */
export function replicateSeatsAcrossConnectionLegs(firstLegSeats = [], trips = []) {
  if (!Array.isArray(trips) || trips.length === 0) {
    return firstLegSeats;
  }

  const replicatedSeats = [];
  firstLegSeats.forEach((seat) => {
    trips.forEach((trip) => {
      replicatedSeats.push({
        ...seat,
        tripSlug: trip.id,
      });
    });
  });
  return replicatedSeats;
}

/**
 * Builds an informational transfer route label from all connection legs.
 * Includes origin, every intermediate transfer city, and the final destination
 * (not just the overall origin/destination pair).
 *
 * Walks each leg in order: first-leg origin, then each leg's destination.
 * Example (2 legs): "BOGOTA → CALI → PASTO"
 * Example (3 legs): "A → B → C → D"
 *
 * @param {Array<object>} trips - Connection trips with terminals + trip.originId/destinationId.
 * @returns {string} Route label, or empty string when trips are missing.
 */
export function getConnectionTransferRouteLabel(trips = []) {
  if (!Array.isArray(trips) || trips.length === 0) {
    return '';
  }

  const cities = [];
  trips.forEach((trip, index) => {
    const originId = trip?.trip?.originId;
    const destinationId = trip?.trip?.destinationId;
    const originCity = trip?.terminals?.[originId]?.cityName;
    const destinationCity = trip?.terminals?.[destinationId]?.cityName;

    if (index === 0 && originCity) {
      cities.push(originCity);
    }
    if (destinationCity) {
      cities.push(destinationCity);
    }
  });

  return cities.join(' → ');
}
